import { useEffect, useMemo, useRef, useState } from "react";
import { GRID_CELLS, generateSudoku } from "@pj/sudoku";

export interface SudokuOverlayProps {
  seed: number;
  /** The grid as last persisted by the sim (81 chars, '0' = empty). */
  entries: string;
  /** Called on every edit — the sim persists it and decides "solved". */
  onEntries: (entries: string) => void;
  onExit: () => void;
}

/**
 * The Level-2 computer minigame: a hard 9×9 sudoku, GBA-flavoured. The puzzle
 * is derived from the seed (same one the sim uses), CHECK highlights wrong
 * cells against the unique solution, and every edit is sent to the sim — which
 * closes the overlay itself the moment the grid is correct.
 */
export function SudokuOverlay({ seed, entries, onEntries, onExit }: SudokuOverlayProps): JSX.Element {
  const puzzle = useMemo(() => generateSudoku(seed), [seed]);
  const [grid, setGrid] = useState<string>(() =>
    entries.length === GRID_CELLS ? entries : puzzle.givens,
  );
  const [sel, setSel] = useState(() => puzzle.givens.indexOf("0"));
  const [wrong, setWrong] = useState<ReadonlySet<number>>(new Set());
  const gridRef = useRef(grid);
  gridRef.current = grid;
  const selRef = useRef(sel);
  selRef.current = sel;

  // Dev/e2e hook: lets the browser flow read the solution instead of solving
  // a hard sudoku by hand. Stripped from production builds.
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>)["__pjSudoku"] = {
        seed,
        givens: puzzle.givens,
        solution: puzzle.solution,
      };
    }
  }, [seed, puzzle]);

  const setCell = (idx: number, digit: number): void => {
    if (puzzle.givens[idx] !== "0") return; // givens are fixed
    const next = gridRef.current.slice(0, idx) + String(digit) + gridRef.current.slice(idx + 1);
    setGrid(next);
    setWrong(new Set());
    onEntries(next);
  };

  const check = (): void => {
    const bad = new Set<number>();
    for (let i = 0; i < GRID_CELLS; i++) {
      const c = gridRef.current[i]!;
      if (c !== "0" && c !== puzzle.solution[i]) bad.add(i);
    }
    setWrong(bad);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const cur = selRef.current;
      const move = (d: number): void => {
        const next = (cur + d + GRID_CELLS) % GRID_CELLS;
        setSel(next);
      };
      if (e.code === "Escape") onExit();
      else if (e.code === "ArrowUp") move(-9);
      else if (e.code === "ArrowDown") move(9);
      else if (e.code === "ArrowLeft") move(-1);
      else if (e.code === "ArrowRight") move(1);
      else if (/^(Digit|Numpad)[1-9]$/.test(e.code)) setCell(cur, Number(e.code.slice(-1)));
      else if (e.code === "Backspace" || e.code === "Delete" || e.code === "Digit0") setCell(cur, 0);
      else return; // everything else falls through (the field is locked anyway)
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puzzle, onExit]);

  const cells = [];
  for (let i = 0; i < GRID_CELLS; i++) {
    const given = puzzle.givens[i] !== "0";
    const value = grid[i] === "0" ? "" : grid[i];
    const classes = ["sud-cell"];
    if (given) classes.push("given");
    if (i === sel) classes.push("sel");
    if (wrong.has(i)) classes.push("wrong");
    if (i % 9 === 2 || i % 9 === 5) classes.push("box-r");
    if (Math.floor(i / 9) === 2 || Math.floor(i / 9) === 5) classes.push("box-b");
    cells.push(
      <button
        key={i}
        type="button"
        data-idx={i}
        className={classes.join(" ")}
        onClick={() => setSel(i)}
      >
        {value}
      </button>,
    );
  }

  return (
    <div className="sudoku-overlay">
      <div className="sudoku-panel">
        <div className="sud-title">SUDOKU — just one game, then bhaav. promise.</div>
        <div className="sud-grid">{cells}</div>
        <div className="sud-digits">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <button key={d} type="button" className="sud-digit" onClick={() => setCell(selRef.current, d)}>
              {d}
            </button>
          ))}
          <button type="button" className="sud-digit erase" onClick={() => setCell(selRef.current, 0)}>
            ⌫
          </button>
        </div>
        <div className="sud-actions">
          <button type="button" className="sud-check" onClick={check}>
            CHECK
          </button>
          <button type="button" className="sud-close" onClick={onExit}>
            CLOSE (progress kept)
          </button>
        </div>
        <div className="sud-hint">Click a cell or use arrows · 1-9 to fill · ⌫ to erase</div>
      </div>
    </div>
  );
}
